/**
 * artworkModel.js
 * Comprehensive Fine Art Metadata & Edition Model for the Artwork Catalog Module.
 * Designed according to fine-art museum and gallery standards (Artlogic, Art Galleria).
 */

export const ARTWORK_CATEGORIES = [
    'Painting',
    'Sculpture',
    'Photography',
    'Limited Edition Print',
    'Drawing & Works on Paper',
    'Mixed Media & Collage',
    'Ceramics & Glass',
    'Digital & Installation'
];

export const EDITION_TYPES = [
    { value: 'unique', label: 'Unique Original (1 of 1)' },
    { value: 'limited_edition', label: 'Limited Edition (Numbered Run)' },
    { value: 'open_edition', label: 'Open Edition (Unnumbered)' },
    { value: 'monoprint', label: 'Monoprint / Monotype' },
    { value: 'variable_edition', label: 'Variable Edition (EV)' }
];

export const ARTWORK_STATUSES = [
    { value: 'available', label: 'Available for Acquisition', color: '#10b981', bg: '#dcfce7' },
    { value: 'reserved', label: 'Reserved / On Hold', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'sold', label: 'Sold (Private Collection)', color: '#64748b', bg: '#f1f5f9' },
    { value: 'consigned', label: 'On Consignment (Partner Gallery)', color: '#6366f1', bg: '#e0e7ff' },
    { value: 'on_loan', label: 'On Museum / Institutional Loan', color: '#8b5cf6', bg: '#ede9fe' },
    { value: 'not_for_sale', label: 'Not for Sale (NFS / Permanent Archive)', color: '#dc2626', bg: '#fee2e2' }
];

// ─── DIMENSION HELPERS ────────────────────────────────────────────────────────
export const inToCm = (val) => {
    if (!val || isNaN(val)) return 0;
    return parseFloat((parseFloat(val) * 2.54).toFixed(1));
};

export const cmToIn = (val) => {
    if (!val || isNaN(val)) return 0;
    return parseFloat((parseFloat(val) / 2.54).toFixed(1));
};

export const formatDimensions = (dims = {}, targetUnit = 'both') => {
    const h = parseFloat(dims.height) || 0;
    const w = parseFloat(dims.width) || 0;
    const d = parseFloat(dims.depth) || 0;
    const sourceUnit = dims.unit || 'in';

    let inH = sourceUnit === 'in' ? h : cmToIn(h);
    let inW = sourceUnit === 'in' ? w : cmToIn(w);
    let inD = sourceUnit === 'in' ? d : cmToIn(d);

    let cmH = sourceUnit === 'cm' ? h : inToCm(h);
    let cmW = sourceUnit === 'cm' ? w : inToCm(w);
    let cmD = sourceUnit === 'cm' ? d : inToCm(d);

    const inStr = inD > 0 ? `${inH} × ${inW} × ${inD} in` : `${inH} × ${inW} in`;
    const cmStr = cmD > 0 ? `${cmH} × ${cmW} × ${cmD} cm` : `${cmH} × ${cmW} cm`;

    if (targetUnit === 'in') return inStr;
    if (targetUnit === 'cm') return cmStr;
    return `${inStr} (${cmStr})`;
};

// ─── TIERED EDITION PRICING ENGINE ───────────────────────────────────────────
export const calculateEditionPrice = (artwork = {}, editionNumber = 1) => {
    const basePrice = parseFloat(artwork.pricing?.price) || 0;
    const tiers = artwork.editionDetails?.priceTiers || [];

    if (!tiers || tiers.length === 0) return basePrice;

    // Tiers structure: [{ from: 1, to: 5, price: 1200 }, { from: 6, to: 15, price: 1500 }]
    const matchedTier = tiers.find(t => editionNumber >= t.from && editionNumber <= t.to);
    if (matchedTier && matchedTier.price) {
        return parseFloat(matchedTier.price);
    }

    return basePrice;
};

// ─── CRYPTOGRAPHIC COA HASH GENERATOR ─────────────────────────────────────────
export const generateCoaHash = (artId = '', title = '') => {
    const seed = `${artId}-${title}-${Date.now()}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    const hex = Math.abs(hash).toString(16).toUpperCase().padStart(8, '0');
    return `COA-${hex.substring(0, 4)}-${hex.substring(4, 8)}-${new Date().getFullYear()}`;
};

// ─── SAMPLE ARTWORK DATA REPOSITORY ───────────────────────────────────────────
export const SAMPLE_ARTWORKS = [
    {
        id: 'art-sample-1',
        title: 'Chromatic Resonance No. 4',
        artist: 'Elena Rostova',
        creationYear: 2026,
        medium: 'Oil, cold wax, and crushed marble on Belgian linen',
        category: 'Painting',
        description: 'An expansive abstract study exploring textural stratification and ambient light resonance. Built up through thirty layers of cold wax and scraped pigments.',
        dimensions: {
            height: 48,
            width: 36,
            depth: 1.5,
            unit: 'in',
            framed: true,
            framedHeight: 50.5,
            framedWidth: 38.5,
            framedDepth: 2.2,
            frameDescription: 'Custom hand-finished walnut float frame'
        },
        weightKg: 8.5,
        images: [
            'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1200&q=85',
            'https://images.unsplash.com/photo-1579783902614-a3fb3927b675?w=1200&q=85',
            'https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=1200&q=85'
        ],
        editionType: 'limited_edition',
        editionDetails: {
            totalEditions: 15,
            currentEditionNumber: 4,
            artistProofs: 2,
            printerProofs: 1,
            isArtistProof: false,
            priceTiers: [
                { from: 1, to: 5, price: 3800 },
                { from: 6, to: 10, price: 4400 },
                { from: 11, to: 15, price: 5200 }
            ],
            editionsList: [
                { number: 1, status: 'sold', collector: 'Private Collector, Geneva', soldPrice: 3800 },
                { number: 2, status: 'sold', collector: 'Corporate Collection, Chicago', soldPrice: 3800 },
                { number: 3, status: 'sold', collector: 'Private Patron, London', soldPrice: 3800 },
                { number: 4, status: 'available', price: 3800 },
                { number: 5, status: 'available', price: 3800 },
                { number: 6, status: 'available', price: 4400 },
                { number: 7, status: 'available', price: 4400 },
                { number: 8, status: 'available', price: 4400 }
            ]
        },
        pricing: {
            price: 3800,
            currency: 'USD',
            isPriceOnInquiry: false,
            framedAddonPrice: 450,
            taxApplicable: true
        },
        status: 'available',
        custody: {
            currentLocation: 'Main Gallery - North Salon',
            locationType: 'on_display',
            consigneeName: '',
            consignmentSplitPct: 50,
            returnDate: ''
        },
        provenance: [
            { year: 2026, text: 'Studio of the Artist, Venice, Italy' },
            { year: 2026, text: 'Curated into "Subliminal Cartographies" Spring Exhibition' }
        ],
        exhibitions: [
            '2026 — Subliminal Cartographies, Contemporary Hall, Zurich',
            '2026 — Art Basel Discovery Sector (Preview)'
        ],
        literature: [
            'Contemporary Abstraction Quarterly, Issue 44, pp. 28-31 with full-plate illustration'
        ],
        conditionReport: {
            grade: 'Mint / Studio Condition',
            notes: 'Pristine surface tension. UV protective satin archival varnish applied. No restoration or handling marks.',
            inspectedDate: '2026-03-12'
        },
        coa: {
            serialNumber: 'COA-8842-1920-2026',
            issuedDate: '2026-03-15',
            signedBy: 'Elena Rostova',
            isDigitalVerified: true
        }
    },
    {
        id: 'art-sample-2',
        title: 'Nocturne in Ultramarine',
        artist: 'Marcus Thorne',
        creationYear: 2025,
        medium: 'Pure mineral pigment, sumi ink, and acrylic binder on raw unprimed cotton duck',
        category: 'Painting',
        description: 'Monumental deep-field canvas rendered in authentic French ultramarine and concentrated sumi ink. An evocative balance of deep void and atmospheric surface vibrancy.',
        dimensions: {
            height: 60,
            width: 72,
            depth: 2.0,
            unit: 'in',
            framed: false,
            frameDescription: 'Unframed gallery wrap, painted edges'
        },
        weightKg: 12.0,
        images: [
            'https://images.unsplash.com/photo-1543857778-c4a1a3e0b2eb?w=1200&q=85',
            'https://images.unsplash.com/photo-1577083552431-6e5fd01aa342?w=1200&q=85'
        ],
        editionType: 'unique',
        editionDetails: {
            totalEditions: 1,
            currentEditionNumber: 1,
            isArtistProof: false
        },
        pricing: {
            price: 8500,
            currency: 'USD',
            isPriceOnInquiry: false,
            framedAddonPrice: 650,
            taxApplicable: true
        },
        status: 'available',
        custody: {
            currentLocation: 'Private Vault / Vault Storage C',
            locationType: 'vault_storage',
            consigneeName: '',
            consignmentSplitPct: 50,
            returnDate: ''
        },
        provenance: [
            { year: 2025, text: 'Created at Berlin Atelier' },
            { year: 2025, text: 'Acquired into Gallery Inventory' }
        ],
        exhibitions: [
            '2025 — Solo Show: Monoliths of Light, Thorne Studio'
        ],
        literature: [
            'Documented in Marcus Thorne Monograph (Vol. II, Plate 89)'
        ],
        conditionReport: {
            grade: 'Mint',
            notes: 'Raw canvas tension optimal. Stretcher bars custom laminated kiln-dried tulipwood.',
            inspectedDate: '2026-01-20'
        },
        coa: {
            serialNumber: 'COA-7731-0091-2025',
            issuedDate: '2025-11-10',
            signedBy: 'Marcus Thorne',
            isDigitalVerified: true
        }
    },
    {
        id: 'art-sample-3',
        title: 'Spatial Geometries I (Equilibrium)',
        artist: 'Sora Takahashi',
        creationYear: 2026,
        medium: 'Lost-wax cast bronze with hand-applied liver of sulfur patina and Nero Marquina marble plinth',
        category: 'Sculpture',
        description: 'Cast bronze construct balancing curvilinear tension against geometric negative space. Each cast is individually hand-patinated with torch-applied ferric nitrates.',
        dimensions: {
            height: 24,
            width: 14,
            depth: 12,
            unit: 'in',
            framed: false,
            frameDescription: 'Mounted on 4 inch Nero Marquina beveled marble base'
        },
        weightKg: 16.5,
        images: [
            'https://images.unsplash.com/photo-1554188248-986adbb73be4?w=1200&q=85',
            'https://images.unsplash.com/photo-1549887552-cb1071d3e5ca?w=1200&q=85'
        ],
        editionType: 'limited_edition',
        editionDetails: {
            totalEditions: 8,
            currentEditionNumber: 3,
            artistProofs: 2,
            priceTiers: [
                { from: 1, to: 4, price: 6200 },
                { from: 5, to: 8, price: 7800 }
            ],
            editionsList: [
                { number: 1, status: 'sold', collector: 'Tokyo Foundation for the Arts', soldPrice: 6200 },
                { number: 2, status: 'sold', collector: 'Private Collector, Kyoto', soldPrice: 6200 },
                { number: 3, status: 'available', price: 6200 },
                { number: 4, status: 'available', price: 6200 },
                { number: 5, status: 'available', price: 7800 }
            ]
        },
        pricing: {
            price: 6200,
            currency: 'USD',
            isPriceOnInquiry: false,
            framedAddonPrice: 0,
            taxApplicable: true
        },
        status: 'available',
        custody: {
            currentLocation: 'Sculpture Pavilion / West Terrace',
            locationType: 'on_display',
            consigneeName: '',
            consignmentSplitPct: 50,
            returnDate: ''
        },
        provenance: [
            { year: 2026, text: 'Cast at Kyoei Foundry, Kyoto, Japan' }
        ],
        exhibitions: [
            '2026 — Modern Sculptural Forms, International Arts Fair'
        ],
        literature: [],
        conditionReport: {
            grade: 'Mint / Foundry Fresh',
            notes: 'Renaissance wax sealed patina. Felt padded base underside.',
            inspectedDate: '2026-02-14'
        },
        coa: {
            serialNumber: 'COA-9921-6541-2026',
            issuedDate: '2026-02-18',
            signedBy: 'Sora Takahashi',
            isDigitalVerified: true
        }
    },
    {
        id: 'art-sample-4',
        title: 'Solitude at Dawn',
        artist: 'Claire Delacroix',
        creationYear: 2025,
        medium: 'Archival pigment print on 310gsm Hahnemühle Photo Rag Bright White',
        category: 'Photography',
        description: 'Captured with large-format analog field camera during early morning fog over coastal Normandy. Printed with monochrome K3 carbon inks.',
        dimensions: {
            height: 30,
            width: 40,
            depth: 0.1,
            unit: 'in',
            framed: true,
            framedHeight: 38,
            framedWidth: 48,
            framedDepth: 1.5,
            frameDescription: 'Matted in 8-ply museum rag with anti-reflective Tru-Vue Museum Glass'
        },
        weightKg: 5.0,
        images: [
            'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1200&q=85',
            'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1200&q=85'
        ],
        editionType: 'limited_edition',
        editionDetails: {
            totalEditions: 25,
            currentEditionNumber: 7,
            artistProofs: 3,
            priceTiers: [
                { from: 1, to: 10, price: 1400 },
                { from: 11, to: 20, price: 1850 },
                { from: 21, to: 25, price: 2400 }
            ]
        },
        pricing: {
            price: 1400,
            currency: 'USD',
            isPriceOnInquiry: false,
            framedAddonPrice: 380,
            taxApplicable: true
        },
        status: 'available',
        custody: {
            currentLocation: 'Print Drawer Cabinet 4',
            locationType: 'flat_file_drawer',
            consigneeName: '',
            consignmentSplitPct: 50,
            returnDate: ''
        },
        provenance: [
            { year: 2025, text: 'Printed under direct artist supervision, Paris' }
        ],
        exhibitions: [
            '2025 — Rencontres d\'Arles Discovery Award Showcase'
        ],
        literature: [],
        conditionReport: {
            grade: 'Mint / Archival Mylar Wrapped',
            notes: 'Acid-free archival tissue slipcased. Embossed artist seal bottom right.',
            inspectedDate: '2025-12-05'
        },
        coa: {
            serialNumber: 'COA-4412-8821-2025',
            issuedDate: '2025-12-08',
            signedBy: 'Claire Delacroix',
            isDigitalVerified: true
        }
    }
];

// ─── WALL SCALER PRESETS & CONFIGURATION ──────────────────────────────────────
export const WALL_ENVIRONMENTS = [
    {
        id: 'living_room',
        name: 'Contemporary Living Room',
        tag: 'Residential Salon',
        ceilingHeightIn: 108, // 9 ft
        defaultWallColor: '#F4F0E8',
        wallTexture: 'smooth',
        floorType: 'light_oak',
        furnitureType: 'sofa',
        lighting: 'daylight'
    },
    {
        id: 'gallery_cube',
        name: 'Minimalist White Cube Gallery',
        tag: 'Museum Space',
        ceilingHeightIn: 120, // 10 ft
        defaultWallColor: '#FFFFFF',
        wallTexture: 'plaster',
        floorType: 'concrete',
        furnitureType: 'bench',
        lighting: 'spotlight'
    },
    {
        id: 'dining_salon',
        name: 'Modern Dining & Salon',
        tag: 'Collector Dining',
        ceilingHeightIn: 108,
        defaultWallColor: '#EAE6DD',
        wallTexture: 'linen',
        floorType: 'herringbone',
        furnitureType: 'dining_table',
        lighting: 'warm'
    },
    {
        id: 'executive_suite',
        name: 'Executive Office & Boardroom',
        tag: 'Corporate Architecture',
        ceilingHeightIn: 114,
        defaultWallColor: '#6B7280',
        wallTexture: 'smooth',
        floorType: 'terrazzo',
        furnitureType: 'credenza',
        lighting: 'daylight'
    },
    {
        id: 'luxury_lounge',
        name: 'Dark Luxury Lounge',
        tag: 'Private Salon',
        ceilingHeightIn: 108,
        defaultWallColor: '#1E2530',
        wallTexture: 'panelling',
        floorType: 'dark_parquet',
        furnitureType: 'armchair',
        lighting: 'evening'
    }
];

export const WALL_COLORS = [
    { name: 'Gallery Pure White', hex: '#FFFFFF', dark: false },
    { name: 'Museum Alabaster', hex: '#F4F0E8', dark: false },
    { name: 'Warm Greige', hex: '#D8D3C8', dark: false },
    { name: 'Flannel Slate', hex: '#7E8B9B', dark: true },
    { name: 'Parisian Navy', hex: '#1E293B', dark: true },
    { name: 'Midnight Charcoal', hex: '#18181B', dark: true },
    { name: 'Terracotta Sienese', hex: '#8A3C26', dark: true },
    { name: 'Sage Umber', hex: '#3F4E38', dark: true }
];

export const FRAME_OPTIONS = [
    {
        id: 'none',
        name: 'No Frame (Gallery Canvas Wrap)',
        borderWidthIn: 0,
        cssBorder: 'none',
        hasDepth: true
    },
    {
        id: 'slim_white',
        name: 'Slim Gallery White',
        borderWidthIn: 0.75,
        cssBorder: '8px solid #FFFFFF',
        cssBackground: '#FFFFFF',
        hasDepth: true
    },
    {
        id: 'natural_oak',
        name: 'Natural Scandinavian Oak',
        borderWidthIn: 1.0,
        cssBorder: '12px solid #C4915C',
        cssBackground: 'linear-gradient(135deg, #DFB282 0%, #C4915C 50%, #B27F4C 100%)',
        hasDepth: true
    },
    {
        id: 'matte_black',
        name: 'Museum Matte Black',
        borderWidthIn: 1.0,
        cssBorder: '12px solid #18181B',
        cssBackground: '#18181B',
        hasDepth: true
    },
    {
        id: 'walnut_espresso',
        name: 'Italian Dark Walnut',
        borderWidthIn: 1.25,
        cssBorder: '14px solid #352112',
        cssBackground: 'linear-gradient(135deg, #4A3322 0%, #352112 50%, #26160A 100%)',
        hasDepth: true
    },
    {
        id: 'gilt_gold',
        name: 'Antiqued Gilt Gold Leaf',
        borderWidthIn: 1.5,
        cssBorder: '16px double #C59B27',
        cssBackground: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 40%, #AA7C11 70%, #D4AF37 100%)',
        hasDepth: true
    }
];

export const MAT_OPTIONS = [
    { id: 'none', name: 'No Mat (Full Bleed)', widthIn: 0, cssPadding: '0px' },
    { id: 'mat_2in', name: '2 in (5 cm) Archival White', widthIn: 2, cssPadding: '20px', bg: '#FAFAF9' },
    { id: 'mat_3in', name: '3 in (7.5 cm) Soft Warm Cream', widthIn: 3, cssPadding: '32px', bg: '#F5F5F0' },
    { id: 'mat_4in', name: '4 in (10 cm) Deep Museum Float', widthIn: 4, cssPadding: '44px', bg: '#FFFFFF' }
];

export const SAMPLE_VIEWING_ROOMS = [
    {
        id: 'ovr-1',
        slug: 'elena-rostova-chromatic-stratifications',
        title: 'Elena Rostova: Chromatic Stratifications',
        subtitle: 'Solo Exhibition & Exclusive Online Premiere',
        curator: 'Dr. Julian Vane, Chief Curator',
        startDate: '2026-10-01',
        endDate: '2026-11-30',
        status: 'live', // 'live' | 'upcoming' | 'closed'
        isVipGated: false,
        vipCode: '',
        heroImage: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=1600&q=85',
        overviewText: 'Elena Rostova’s latest cycle of monumental canvases synthesizes thirty years of rigorous chromatic investigation. Working between Venice and Paris, Rostova builds geological stratifications of cold wax, Venetian marble dust, and crushed earth pigments that reveal subtle luminescence under natural daylight.',
        curatorEssay: 'Elena Rostova’s monumental canvases confront the viewer not merely as aesthetic surfaces, but as geological entities. Each work is synthesized across thirty to fifty distinct layers of cold beeswax, ground Venetian marble, and concentrated mineral pigments.\n\n"Color in this body of work ceases to be an adornment; it behaves as physical matter, vibrating under shifting ambient illumination."\n\nVisitors are invited to experience these works either in sequential exhibition walkthrough or by placing them directly within diverse architectural interiors using our interactive Wall Scaler visualizer.',
        audioGuideTitle: 'Curator Audio Walkthrough (Elena Rostova)',
        audioGuideDuration: '4:18',
        featuredArtworkIds: ['art-sample-1', 'art-sample-2', 'art-sample-3'],
        curatorNotes: {
            'art-sample-1': 'The central thesis painting of the suite. Its cold wax density shifts between matte chalk and translucent depth as lighting angles change.',
            'art-sample-2': 'Executed in authentic historical French ultramarine, investigating the threshold of human optical saturation.',
            'art-sample-3': 'A rare suite of signed archival stone lithographs printed on Japanese Awagami paper.'
        },
        installationViews: [
            'https://images.unsplash.com/photo-1582555172866-f73bb12a2ab3?w=1200&q=80',
            'https://images.unsplash.com/photo-1577083552431-6e5fd01aa342?w=1200&q=80'
        ]
    },
    {
        id: 'ovr-2',
        slug: 'the-sculpted-void',
        title: 'The Sculpted Void: Spatial Minimalists',
        subtitle: 'Private Collector Salon & VIP Preview',
        curator: 'Hélène Moreau, International Curatorial Advisor',
        startDate: '2026-11-05',
        endDate: '2026-12-24',
        status: 'upcoming',
        isVipGated: true,
        vipCode: 'VIP2026',
        heroImage: 'https://images.unsplash.com/photo-1544717305-2782549b5136?w=1600&q=85',
        overviewText: 'An exclusive preview examining the dialogue between monolithic mass and architectural negative space. Featuring bronze casts, hand-carved travertine forms, and deep monochrome studies.',
        curatorEssay: 'A curated invitation into the quiet architecture of silence. In "The Sculpted Void", volume is defined by the absence that cradles it.\n\nFeaturing works acquired directly from studio reserves prior to institutional touring.',
        audioGuideTitle: 'VIP Welcome & Spatial Commentary',
        audioGuideDuration: '3:45',
        featuredArtworkIds: ['art-sample-2', 'art-sample-1'],
        curatorNotes: {
            'art-sample-2': 'Selected for its architectural silence and profound acoustic resonance within gallery rooms.'
        },
        installationViews: [
            'https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=1200&q=80'
        ]
    }
];

// ─── DEFAULT MODULE CONFIGURATION ─────────────────────────────────────────────
export const DEFAULT_ARTWORK_CONFIG = {
    enabled: false,
    isLocked: true,
    addOnPrice: '$39/mo',
    licenseKey: '',
    licenseTier: '',
    slug: 'artworks',
    currency: 'USD',
    galleryName: 'Art Galleria & Studio',
    displayMode: 'masonry', // 'masonry' | 'grid' | 'cards' | 'editorial'
    showPrices: 'always', // 'always' | 'inquire_only' | 'logged_in'
    defaultUnit: 'in', // 'in' | 'cm' | 'both'
    artworks: SAMPLE_ARTWORKS,
    viewingRooms: [],
    categories: ARTWORK_CATEGORIES
};

// ─── LICENSE / PAYWALL CLEARANCE HELPER ───────────────────────────────────────
export const isArtworkCatalogUnlocked = (config = {}) => {
    const isDemo = typeof window !== 'undefined' && (
        localStorage.getItem('omnivaso_demo_mode') === 'true' ||
        (typeof window.location !== 'undefined' && window.location.search.includes('demo=true'))
    );
    const mod = config?.modules?.artworkCatalog || DEFAULT_ARTWORK_CONFIG;
    if (mod.enabled === false) {
        return false;
    }
    // 'OMNI-ART-PRO-ACTIVATED' was the default mock placeholder; treat as locked unless user explicitly licensed or in demo mode
    const hasValidLicense = Boolean(mod.licenseKey && mod.licenseKey !== 'OMNI-ART-PRO-ACTIVATED');
    if (mod.isLocked !== false || !hasValidLicense) {
        return isDemo ? true : false;
    }
    return true;
};

